import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";
import { useEffect, useRef } from "react";

// Enhanced hook to robustly clean up pointer-events (same as Dialog)
function usePointerEventsCleanup(open?: boolean) {
    const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const observerRef = useRef<MutationObserver | null>(null);

    useEffect(() => {
        // Setup MutationObserver to watch for pointer-events changes
        const observer = new MutationObserver(() => {
            // Check if dialog is closed but pointer-events is still set
            const dialogs = document.querySelectorAll('[role="alertdialog"], [role="dialog"]');
            const hasOpenDialogs = Array.from(dialogs).some(
                (dialog) => dialog.getAttribute('data-state') === 'open'
            );

            if (!hasOpenDialogs && document.body.style.pointerEvents === 'none') {
                if (process.env.NODE_ENV === 'development') console.log('[AlertDialog] Cleaning up stale pointer-events: none');
                document.body.style.pointerEvents = '';
            }
        });

        // Observe changes to body style attribute
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['style']
        });

        observerRef.current = observer;

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        // When dialog closes (open becomes false), ensure cleanup after animation
        if (open === false) {
            // Clear any existing timeout
            if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
            }

            // Set timeout to clean up after dialog close animation (300ms)
            cleanupTimeoutRef.current = setTimeout(() => {
                const dialogs = document.querySelectorAll('[role="alertdialog"], [role="dialog"]');
                const hasOpenDialogs = Array.from(dialogs).some(
                    (dialog) => dialog.getAttribute('data-state') === 'open'
                );

                if (!hasOpenDialogs) {
                    if (process.env.NODE_ENV === 'development') console.log('[AlertDialog] Cleaning up pointer-events after dialog close');
                    document.body.style.pointerEvents = '';
                }
            }, 350);
        }

        return () => {
            if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
            }
        };
    }, [open]);

    // Final cleanup on unmount
    useEffect(() => {
        return () => {
            // Use setTimeout to ensure this runs after Radix cleanup
            setTimeout(() => {
                const dialogs = document.querySelectorAll('[role="alertdialog"], [role="dialog"]');
                const hasOpenDialogs = Array.from(dialogs).some(
                    (dialog) => dialog.getAttribute('data-state') === 'open'
                );

                if (!hasOpenDialogs) {
                    if (process.env.NODE_ENV === 'development') console.log('[AlertDialog] Final cleanup on unmount');
                    document.body.style.pointerEvents = '';
                }
            }, 100);
        };
    }, []);
}

// Wrapper component for AlertDialog.Root that adds pointer-events cleanup
const AlertDialogRoot: React.FC<React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Root>> = (props) => {
    usePointerEventsCleanup(props.open);
    return <AlertDialogPrimitive.Root {...props} />;
};

const AlertDialog = AlertDialogRoot;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    />
  </AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold", className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonVariants(), className)}
    {...props}
  />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      buttonVariants({ variant: "outline" }),
      "mt-2 sm:mt-0",
      className
    )}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
